import type { JSX, ReactNode } from "react";

import { Loader } from "lucide-react";

import type { PersonaDiagnosis } from "../api/personaTypes";

export interface DiagnosisShellProps {
  /** A fetch or a diagnosis run is in flight. */
  loading: boolean;
  /** The first fetch attempt has completed, successfully or not. */
  fetched: boolean;
  /** The diagnosis, or null when this persona has never been diagnosed. */
  diagnosis: PersonaDiagnosis | null;
  /** Message from a failed fetch or run. */
  error: string | null;
  /** Kick off a diagnosis for this persona. */
  onStart: () => void;
  personaLabel: string;
  emoji: string;
  /** The persona's visibility score, which gates the "below 50" nudge. */
  score: number;
  /** Rendered once a diagnosis exists. */
  children: ReactNode;
}

/**
 * Loading / failed / not-yet-run states for a persona diagnosis.
 *
 * Kept separate from the Recommendations tab because none of these states have anything to do with
 * recommendations: they cover the window before a diagnosis exists at all, including the call to
 * action that creates one.
 */
const DiagnosisShell = ({
  loading,
  fetched,
  diagnosis,
  error,
  onStart,
  personaLabel,
  emoji,
  score,
  children,
}: DiagnosisShellProps): JSX.Element => {
  // Show spinner before the first fetch attempt completes
  if (!fetched) {
    return (
      <div className="flex items-center justify-center gap-3 py-8 text-on-surface-variant">
        <Loader className="w-5 h-5 animate-spin" />
        <span className="text-sm">Loading diagnosis…</span>
      </div>
    );
  }

  if (diagnosis?.status === "failed") {
    return (
      <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30 text-sm text-red-400">
        Couldn&apos;t diagnose this persona — add a website to your business profile, then{" "}
        <button onClick={onStart} disabled={loading} className="underline">
          try again
        </button>
        .
      </div>
    );
  }

  if (diagnosis?.status === "processing" || (loading && !diagnosis)) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center justify-center gap-3 py-8 text-center text-on-surface-variant"
      >
        <Loader className="w-5 h-5 animate-spin" />
        <div>
          <p className="text-sm">Diagnosing your website…</p>
          <p className="text-xs mt-1">This usually takes 5–10 minutes.</p>
        </div>
      </div>
    );
  }

  if (!diagnosis) {
    return (
      <div className="text-center py-6">
        {score < 50 && (
          <p className="text-sm text-on-surface-variant mb-3">
            Score is below 50 — run a diagnosis to find gaps and get step-by-step fixes.
          </p>
        )}
        <p className="text-xs text-on-surface-variant mb-3">
          This diagnosis runs against your website and usually takes 5–10 minutes.
        </p>
        <button
          onClick={onStart}
          disabled={loading}
          className="px-4 py-2 bg-primary text-on-primary rounded-xl text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          Diagnose &amp; Fix {emoji} {personaLabel}
        </button>
        {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
      </div>
    );
  }

  return <>{children}</>;
};

export default DiagnosisShell;
